import { Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  useLessonFeedback,
  useLessonRating,
  useMyLessonFeedback,
  useSubmitFeedback,
} from '@/hooks/use-feedback'

interface FeedbackFormProps {
  lessonId: string
  evaluationPrompt?: string | null
}

export function FeedbackForm({ lessonId, evaluationPrompt }: FeedbackFormProps) {
  const { data: ratingSummary, isLoading: ratingLoading } = useLessonRating(lessonId)
  const { data: feedbackList, isLoading: feedbackLoading } = useLessonFeedback(lessonId)
  const { data: myFeedbackData, isLoading: myFeedbackLoading } = useMyLessonFeedback(lessonId)
  const submitFeedback = useSubmitFeedback()

  const myFeedback = myFeedbackData as any
  const hasMyFeedback = !!myFeedback?.id

  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [comment, setComment] = useState('')
  const [initialized, setInitialized] = useState(false)

  // 自分の既存評価でフォームを初期化
  useEffect(() => {
    if (!myFeedbackLoading && !initialized) {
      if (myFeedback?.id) {
        setRating(myFeedback.rating ?? 0)
        setComment(myFeedback.comment ?? '')
      }
      setInitialized(true)
    }
  }, [myFeedback, myFeedbackLoading, initialized])

  const summary = ratingSummary as any
  const feedbacks = (feedbackList as any)?.data ?? (feedbackList as any) ?? []

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('評価を選択してください')
      return
    }
    try {
      await submitFeedback.mutateAsync({
        lessonId,
        data: { rating, comment: comment.trim() || undefined },
      })
      toast.success(hasMyFeedback ? '評価を更新しました' : '評価を送信しました')
    } catch {
      toast.error('送信に失敗しました')
    }
  }

  if (ratingLoading || feedbackLoading || myFeedbackLoading) return <Skeleton className="h-48" />

  return (
    <div className="space-y-4">
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">レッスン評価</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                <span className="text-2xl font-bold">{summary.average?.toFixed(1) ?? '-'}</span>
              </div>
              <span className="text-sm text-muted-foreground">({summary.count ?? 0}件の評価)</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{hasMyFeedback ? '評価を変更' : '評価を送信'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {evaluationPrompt && (
            <p className="text-sm italic text-muted-foreground">{evaluationPrompt}</p>
          )}
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
              >
                <Star
                  className={`h-8 w-8 ${
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
          <Textarea
            placeholder="コメント（任意）"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
          <Button onClick={handleSubmit} disabled={submitFeedback.isPending}>
            {submitFeedback.isPending ? '送信中...' : hasMyFeedback ? '更新' : '送信'}
          </Button>
        </CardContent>
      </Card>

      {/* Existing feedback comments */}
      {Array.isArray(feedbacks) && feedbacks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">コメント一覧</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {feedbacks.map((item: any) => {
              const fb = item.feedback ?? item
              return (
                <div key={fb.id} className="border-b pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`h-3 w-3 ${
                            s <= fb.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(fb.createdAt).toLocaleDateString('ja-JP')}
                    </span>
                  </div>
                  {fb.comment && <p className="mt-1 text-sm">{fb.comment}</p>}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
